import {useWorkOrdersSummary} from '@domain/work-orders'
import {WorkOrdersSummaryChart} from '@hconnect/common/components'
import {DataContentWrapper} from '@hconnect/common/containers'
import {CardBox} from '@hconnect/uikit/src/lib2'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {spawnErrorToast} from '@hm/fundamental'
import {CalendarTodayOutlined} from '@mui/icons-material'
import {Box, Chip, SxProps, Theme, Typography} from '@mui/material'
import moment from 'moment-timezone'
import {useCallback} from 'react'
import {useTranslation} from 'react-i18next'

import {WORK_ORDERS_WIDGET_NAMESPACE} from '../consts'

type Props = {
  plantId: string
  cardBoxSx?: SxProps<Theme>
}

export const WorkOrdersSummaryContainer = ({plantId, cardBoxSx}: Props) => {
  const {t} = useTranslation(WORK_ORDERS_WIDGET_NAMESPACE)
  const timezone = useUpmPlantTimezone(plantId)
  const startOfToday = moment.utc().tz(timezone).startOf('day')
  const thirtyDaysAgo = startOfToday.clone().subtract(30, 'day')
  const endOfToday = startOfToday.clone().endOf('day')

  const notifyFailedWorkOrders = useCallback(
    () => spawnErrorToast(t('message.failedToFetchWorkOrdersSummary')),
    [t]
  )

  const {data, isLoading, isError, isRefetching, refetch} = useWorkOrdersSummary(
    {
      plantId,
      from: thirtyDaysAgo,
      to: endOfToday
    },
    {onError: notifyFailedWorkOrders}
  )

  return (
    <CardBox
      data-test-id="work-order-summary-card"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        ...cardBoxSx,
        px: 0,
        pl: 3
      }}
    >
      <Box display="flex" flexDirection="row">
        <Box flex={1}>
          <Typography variant="h3" mb={3}>
            {t('workOrderSummary.cardTitle')}
          </Typography>
        </Box>
        <Chip
          icon={<CalendarTodayOutlined />}
          label={t('label.last30days')}
          size="small"
          sx={{mx: 1, mr: 4}}
        />
      </Box>
      <DataContentWrapper
        showLoader={isLoading}
        isError={isError}
        isRefetching={isRefetching}
        data={data}
        retryFunction={() => void refetch()}
        progressSx={{color: 'primary.main'}}
      >
        <Box sx={{flex: 1, overflowY: 'auto', pr: 3}}>
          <WorkOrdersSummaryChart data={data} t={t} />
        </Box>
      </DataContentWrapper>
    </CardBox>
  )
}
